<template>
  <div
    class="relative single-select"
    ref="container"
    :class="disabledClass"
  >
    <!-- Selected box -->
    <div v-if="!showNothing"
        class="border rounded-lg p-2 text-sm w-full flex justify-between items-center min-h-[40px]"
        :class="[!disabled ? 'cursor-pointer' : '', disabledClass]"
        :style="selectedStyle"
        @click="!disabled && toggleDropdown()"
      >
      <span>{{ selectedLabel }}</span>

      <svg
        v-if="!disabled"
        class="w-4 h-4 ml-2 transform transition-transform"
        :class="{ 'rotate-180': open }"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M19 9l-7 7-7-7"
        />
      </svg>
    </div>

    <!-- Dropdown list -->
    <div
      v-if="open && !disabled"
      ref="dropdown"
      class="absolute z-[9999] w-full border rounded-lg shadow-lg bg-white max-h-48 overflow-auto"
	  style="width: max-content; min-width:150px"
      :class="dropUp ? 'bottom-full mb-1' : 'top-full mt-1'"
    >
      <div
        v-for="(opt, index) in options"
        :key="opt.id"
        class="p-2 cursor-pointer transition-all duration-150 m-1" 
        :class="{
          // Apply a prominent border/ring and darken the boundary
          'ring-2 ring-gray-800 ring-inset rounded-md font-semibold': opt.id === modelValue,
          'hover:bg-gray-100': opt.id !== modelValue
        }"
        :style="optionStyle(opt, opt.id === modelValue)"
        @click="selectOption(opt)"
      >
        {{ opt.label }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { onClickOutside } from '@vueuse/core'

const props = defineProps({
  modelValue: [String, Number],
  options: {
    type: Array,
    required: true
  },
  /** When set (e.g. premium RG status), used to resolve label/colors for values not in `options` (read-only / legacy DB values). */
  displayOptions: {
    type: Array,
    default: null
  },
  fontSize: {
    type: [String, Number],
    default: null
  },
  disabled: {
    type: Boolean,
    default: false
  },
  showNothing : {
    type: Boolean,
    default: false
  },
  dimWhenDisabled: {
    type: Boolean,
    default: false
  },
  onUpdate: {
    type: Function,
    default: () => () => {}
  }
})

const emit = defineEmits(['update:modelValue'])

const open = ref(false)
const dropUp = ref(false)

const container = ref(null)
const dropdown = ref(null)

// -------------------------
// Selected option
// -------------------------
const selected = computed(() => {
  const opts = props.options || []
  const fromOpts = opts.find(o => o.id == props.modelValue)
  if (fromOpts) return fromOpts
  const disp = props.displayOptions || []
  return disp.find(o => o.id == props.modelValue)
})

const selectedLabel = computed(() => {
  if (selected.value?.label != null && selected.value?.label !== '') return selected.value.label
  // Fallback: if options are missing / value not in options, still show the raw value.
  return props.modelValue == null ? '' : String(props.modelValue)
})

const selectedStyle = computed(() => {
  const opt = selected.value
  return {
    backgroundColor: opt?.bgcolor || 'white',
    color: opt?.color || 'black',
    fontSize: props.fontSize
      ? typeof props.fontSize === 'number'
        ? props.fontSize + 'px'
        : props.fontSize
      : undefined
  }
})

const disabledClass = computed(() => {
  if (props.disabled && props.dimWhenDisabled) {
    // Changed 'cursor-not-allowed' to 'cursor-default'
    return 'opacity-50 cursor-default'
  } else if (props.disabled) {
    // Changed 'cursor-not-allowed' to 'cursor-default'
    return 'cursor-default'
  }
  return ''
})

// -------------------------
// Option styling
// -------------------------
function optionStyle(opt, isSelected = false) {
  return {
    // Keep original color even if selected
    backgroundColor: opt.bgcolor || 'white', 
    color: opt.color || 'black',
    fontSize: props.fontSize
      ? typeof props.fontSize === 'number'
        ? props.fontSize + 'px'
        : props.fontSize
      : undefined
  }
}

function lightenColor(color, amount) {
  try {
    let col = color.startsWith('#')
      ? color.slice(1)
      : color
    if (col.length === 3) col = col.split('').map(c => c + c).join('')
    const num = parseInt(col, 16)
    let r = Math.min(255, (num >> 16) + 255 * amount)
    let g = Math.min(255, ((num >> 8) & 0x00FF) + 255 * amount)
    let b = Math.min(255, (num & 0x0000FF) + 255 * amount)
    return `rgb(${r}, ${g}, ${b})`
  } catch {
    return color
  }
}

// -------------------------
// Dropdown open / close
// -------------------------
function toggleDropdown() {
  open.value = !open.value

  if (open.value) {
    requestAnimationFrame(() => {
      recalcDropdownPosition()
    })
  }
}

function selectOption(opt) {
  if (props.disabled) return
  emit('update:modelValue', opt.id)
  open.value = false
}

// Close on outside click
onClickOutside(container, () => (open.value = false))

// -------------------------
// Auto-flip calculation
// -------------------------
function recalcDropdownPosition() {
  if (!container.value) return

  const rect = container.value.getBoundingClientRect()
  const dropdownHeight = 200 // ~ max-h-48 (12rem)

  const spaceBelow = window.innerHeight - rect.bottom
  const spaceAbove = rect.top

  dropUp.value = spaceBelow < dropdownHeight && spaceAbove > spaceBelow
}

// Recalculate on resize + scroll
function handleRecalc() {
  if (open.value) recalcDropdownPosition()
}

onMounted(() => {
  window.addEventListener('resize', handleRecalc)
  window.addEventListener('scroll', handleRecalc, true)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleRecalc)
  window.removeEventListener('scroll', handleRecalc, true)
})
</script>

<style scoped>
/* Optional — helps parent to avoid clipping */
.single-select {
  overflow: visible;
}
</style>
